import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useUserAuth from '../../../../utils/useUserAuth';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Button, ButtonBase, CircularProgress, Popover, Tooltip } from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CommonDatePicker from '../../../../form-components/CommonDatePicker';
import FilterListIcon from '@mui/icons-material/FilterList';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import moment from 'moment';
import { errorMessages } from '../../../../utils/methods';
import {
  getMemberGroupAttendance,
  getMemberGroupAttendDays,
  getMemberGroupVisits,
} from '../../../../api/member-group';
import { CommonExcelButton } from '../../../../form-components/CommonExcelButton';
import {
  getBranchCustomOptions,
  createBranchCustomOption,
  deleteBranchCustomOption,
} from '../../../../api/branch-custom-option';
import {
  PeriodChip,
  PeriodChipRow,
  AttendanceSummaryCard,
  AttendanceKpi,
  AttendanceKpiValue,
  AttendanceKpiLabel,
  AttendanceKpiHint,
  AttendanceKpiDivider,
  StatRingWrap,
  StatRingCenter,
  ModeSwitch,
  ModeSwitchButton,
  AttendanceTabsBar,
  MRow,
  TableTopRow,
  CLASS_GRID,
  HintMark,
  WideOverlay,
  WideToggle,
  AttendanceMain,
  ClassTypeChip,
  GroupClassChip,
  ClassSectionRow,
  PresetArrow,
  PresetEdgeArrow,
  PresetViewport,
  PresetSlot,
  PresetRemove,
  PresetAmountInput,
  StatusSelect,
  UsageRow,
  AttendanceViewTabs,
  AttendanceViewTab,
  EntrySourceBadge,
  LateBadge,
  ExpandChevron,
  PeriodButton,
  MemberStatName,
  AttendanceHeaderRow,
  AttendanceRow,
  AttendanceStatusBadge,
  AttendanceTypeBadge,
  AttendanceClassLink,
  AttendanceTimeCell,
  MemberFilterRow,
  MemberFilterChip,
  MemberFilterCount,
  FilterIconButton,
  ConditionPanel,
  ConditionGrid,
  ConditionBlock,
  ConditionLabel,
  ConditionChipRow,
  ConditionEmpty,
  VisitRow,
  VisitLinkedMark,
  EmptyState,
  EmptyTitle,
  EmptyDescription,
} from './MemberGroupModalStyled';

import {
  STATUS_LABEL,
  STATUS_VIEW_OPTIONS,
  matchesStatusView,
  PEOPLE_CHIP_LIMIT,
  dayDateLabel,
  TYPE_LABEL,
  validatePresetRange,
  validateRelativePreset,
  relativePresetLabel,
  PRESET_DIRECTIONS,
  PRESET_UNITS,
  BUILT_IN_PRESETS,
  DEFAULT_PRESET_KEY,
  presetRangeText,
  presetRangeOf,
  toPreset,
  entryToneOf,
  entryLabelOf,
  TICKET_TYPE_LABEL,
  stayLabel,
  dateWithDay,
  classNameOf,
  EMPTY_ROWS,
  RENDER_CHUNK,
  SESSION_CHIP,
} from './attendanceShared';
import { downloadAttendanceExcel } from './attendanceExcel';
import { buildAttendanceBook } from './attendanceBook';
import AttendanceBookView from './AttendanceBookView';


/**
 * 그룹 관리 모달의 [출결] 탭. 기간 내 그룹 회원들의 예약/출석/결석 내역 표 + 엑셀 다운로드.
 */
const RING_R = 24;
const RING_C = 2 * Math.PI * RING_R;
// 스킬 기준: 마이크로 인터랙션은 500ms 를 넘기지 않는다. 게이지 한 바퀴라 420ms 로 둔다.
const RING_MS = 420;

/*
 * 출석률 링 (#92). 게이지가 차오르는 동안 숫자도 같이 오른다 — 값이 바뀐 걸 눈이
 * 따라가게 하는 장치라 둘이 어긋나면 안 되므로 하나의 진행값에서 같이 그린다.
 *
 * 매 프레임 다시 그리기 때문에 탭 본체가 아니라 이 작은 컴포넌트만 리렌더되게 떼어냈다.
 * 움직임을 끈 사용자에게는 곧장 최종값을 보여준다(prefers-reduced-motion).
 */
const AttendanceRing = ({ rate, label }) => {
  const target = rate == null ? 0 : rate;
  const [shown, setShown] = useState(target);
  const fromRef = React.useRef(target);

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const from = fromRef.current;
    if (reduce || from === target) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = now => {
      const progress = Math.min(1, (now - start) / RING_MS);
      const eased = 1 - (1 - progress) ** 3; // ease-out — 선형은 기계처럼 읽힌다
      setShown(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      // 도중에 값이 또 바뀌면 지금 보이는 지점에서 이어 간다. 0 으로 튀지 않게.
      fromRef.current = shown;
      cancelAnimationFrame(raf);
    };
    // shown 을 의존성에 넣으면 매 프레임 효과가 다시 걸린다. target 만 본다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return (
    // 읽어 주는 값은 최종값이다. 올라가는 중간 숫자를 낭독하면 소음이 된다.
    <StatRingWrap className="attendance-ring" role="img" aria-label={label}>
      <svg width="58" height="58" viewBox="0 0 58 58">
        <circle cx="29" cy="29" r={RING_R} fill="none" stroke="#E5EAEE" strokeWidth="7" />
        {shown > 0 && (
          <circle cx="29" cy="29" r={RING_R} fill="none" stroke="#14A79C" strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${(shown / 100) * RING_C} ${RING_C}`}
            transform="rotate(-90 29 29)" />
        )}
      </svg>
      <StatRingCenter>{rate == null ? '-' : `${shown}%`}</StatRingCenter>
    </StatRingWrap>
  );
};

/*
 * 헷갈리는 칸에만 물음표를 붙인다. 전부 달면 아무것도 안 읽힌다.
 * 문구는 규정문이 아니라 옆에서 말해 주는 투로 쓴다.
 */
const HINTS = {
  rate: '지난 수업만 계산해요. 지각도 출석으로 치고, 예정은 빠집니다.',
  reserved: '아직 날짜가 안 지난 예약이에요.',
};

const Hint = ({ text }) => (
  <Tooltip arrow placement="top" title={text}>
    <HintMark aria-label={text}>?</HintMark>
  </Tooltip>
);

/*
 * 방문 구분과 상품 축은 서로 성립하지 않는 조합이 있다.
 * 이용권은 자유 입장 상품이라 수업 예약 방문에 붙지 않고, 수강권은 시설 자유 입장에 붙지 않는다.
 * 고를 수는 있는데 결과가 늘 0건이면 필터가 고장난 것처럼 보이므로 아예 감춘다.
 * 일일권은 예약도 입장도 되므로 어느 구분에서도 남긴다.
 */
const VISIT_TICKET_OPTIONS = [
  ['all', '상품 전체'],
  ['COURSE_PASS', '이용권'],
  ['COURSE_TICKET', '수강권'],
  ['TRIAL_TICKET', '일일권'],
];

const HIDDEN_TICKET_KIND_BY_VISIT = {
  class: 'COURSE_PASS',
  facility: 'COURSE_TICKET',
};

const MemberGroupAttendanceTab = ({ group,
  members = [], onNavigate, keyword: rawKeyword = '' }) => {
  // 회원명·전화번호·출결이 통째로 나가는 파일이라 회원목록 엑셀과 같은 권한으로 잠근다.
  const canExcel = useUserAuth('MEMBER_EXCEL_DOWNLOAD');

  /*
   * 검색어는 한 박자 늦춘다(회원 탭과 같은 300ms). 출결은 한 글자마다 필터 + 회원별 집계 +
   * 표 전체 리렌더가 도는데, 행이 수천이면 타이핑이 끊긴다.
   */
  const [keyword, setKeyword] = useState(rawKeyword.trim());
  useEffect(() => {
    const timer = setTimeout(() => setKeyword(rawKeyword.trim()), 300);
    return () => clearTimeout(timer);
  }, [rawKeyword]);
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.user);

  // 오늘·이번 주·이번 달은 늘 있는 버튼이다. 그중 이번 달로 연다.
  const initial = presetRangeOf(
    BUILT_IN_PRESETS.find(p => p.key === DEFAULT_PRESET_KEY));
  const [preset, setPreset] = useState(DEFAULT_PRESET_KEY);
  const [startDate, setStartDate] = useState(initial.start.format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(initial.end.format('YYYY-MM-DD'));

  const validPeriod = !!startDate && !!endDate && startDate <= endDate;

  /*
   * 기간 버튼은 지점이 만든다 (#93). '전체' 는 데이터가 아니라 늘 맨 앞에 붙는 상수다.
   */
  const branchId = user?.branch?.branchId;
  const queryClient = useQueryClient();
  const { data: presetOptions } = useQuery({
    queryKey: ['branchPeriodPresets', branchId],
    queryFn: () => getBranchCustomOptions(user.token, branchId, 'PERIOD_PRESET'),
    enabled: !!branchId && !!user?.token,
  });
  const presets = useMemo(
    () => [...BUILT_IN_PRESETS, ...(presetOptions || EMPTY_ROWS).map(toPreset)],
    [presetOptions],
  );

  const [presetAnchor, setPresetAnchor] = useState(null);
  const [newMode, setNewMode] = useState('relative'); // 'relative' | 'fixed'
  const [newLabel, setNewLabel] = useState('');
  const [newDirection, setNewDirection] = useState('PAST');
  const [newAmount, setNewAmount] = useState('3');
  const [newUnit, setNewUnit] = useState('MONTH');
  const [newStart, setNewStart] = useState(null);
  const [newEnd, setNewEnd] = useState(null);
  const presetRowRef = React.useRef(null);
  const [presetScroll, setPresetScroll] = useState({ left: false, right: false });

  const invalidatePresets = () =>
    queryClient.invalidateQueries({ queryKey: ['branchPeriodPresets', branchId] });

  const addPresetMutation = useMutation({
    mutationFn: body => createBranchCustomOption(user.token, branchId, body),
    onSuccess: () => {
      setPresetAnchor(null);
      setNewLabel('');
      setNewStart(null);
      setNewEnd(null);
      invalidatePresets();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const removePresetMutation = useMutation({
    mutationFn: optionId => deleteBranchCustomOption(user.token, branchId, optionId),
    onSuccess: (_data, optionId) => {
      // 지금 보고 있던 버튼이 사라졌으면 선택만 푼다. 기간은 그대로 두어 표가 흔들리지 않는다.
      if (preset === `p-${optionId}`) setPreset(null);
      invalidatePresets();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const presetError = newMode === 'relative'
    ? validateRelativePreset(newAmount, newUnit)
    : validatePresetRange(newLabel, newStart, newEnd);
  // 상대 기간은 이름을 안 적으면 규칙에서 만들어 준다. 고정 기간은 이름이 곧 전부라 필수다.
  const autoLabel = relativePresetLabel(newDirection, newAmount, newUnit);

  // 프리셋이 넘칠 때만 화살표를 켠다. 늘 떠 있으면 누를 데가 없는 버튼이 둘 생긴다.
  const syncPresetScroll = () => {
    const node = presetRowRef.current;
    if (!node) return;
    setPresetScroll({
      left: node.scrollLeft > 4,
      right: node.scrollLeft + node.clientWidth < node.scrollWidth - 4,
    });
  };
  useEffect(() => {
    syncPresetScroll();
  }, [presets]);

  const scrollPresets = direction => {
    const node = presetRowRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * Math.max(160, node.clientWidth * 0.6), behavior: 'smooth' });
  };

  const { data: rows = EMPTY_ROWS, isLoading, isError } = useQuery({
    queryKey: ['memberGroupAttendance', group.id, startDate, endDate],
    queryFn: () => getMemberGroupAttendance(user.token, group.id, startDate, endDate),
    enabled: !!group.id && validPeriod,
  });

  /*
   * 2단 구조 (2026-08-26 사용자 확정). 수강권·체험 수강권은 예약하고 방문하는 상품이라
   * 출석/결석/지각이 의미 있지만, 이용권·체험 이용권은 기간 내 자유 입장이라 출결 개념이
   * 없다 — 얼마나 자주 오고, 얼마나 머물고, 무엇을 쓰는지가 중요하다.
   * class(수업 출결) = rows/members/classes, facility(시설 이용) = visits/book/usage.
   */
  const [mode, setMode] = useState('class'); // class | facility
  const [view, setView] = useState('rows'); // rows | members | classes | visits | book | usage

  const switchMode = nextMode => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setFilterOpen(false);
    setView(nextMode === 'class' ? 'rows' : 'visits');
  };

  /*
   * 방문(입장) 내역. 이용권 입장은 수업 예약이 없어 출결 목록에 안 잡히므로 따로 받는다.
   * 방문 탭을 열었을 때만 부른다 — 안 볼 사람에게까지 요청을 태울 이유가 없다.
   */
  // 출석부는 입장기록이 있어야 '방문'을 알 수 있다. 그 뷰에서만 부른다.
  const { data: attendDays = EMPTY_ROWS, isLoading: attendDaysLoading } = useQuery({
    queryKey: ['memberGroupAttendDays', group.id, startDate, endDate],
    queryFn: () => getMemberGroupAttendDays(user.token, group.id, startDate, endDate),
    enabled: !!group.id && validPeriod && view === 'book',
  });

  const { data: visits = EMPTY_ROWS, isLoading: visitsLoading, isError: visitsError } = useQuery({
    queryKey: ['memberGroupVisits', group.id, startDate, endDate],
    queryFn: () => getMemberGroupVisits(user.token, group.id, startDate, endDate),
    enabled: !!group.id && validPeriod && (view === 'visits' || view === 'usage'),
  });

  // 기간 안에서 다시 좁히는 조건. 행을 이미 다 받아 두므로 화면에서 거른다.
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusView, setStatusView] = useState(''); // 표 위 드롭다운. 비면 전체
  const [wide, setWide] = useState(false); // 크게 보기

  // 크게 보기는 화면을 덮으므로 ESC 로 빠져나갈 수 있어야 한다.
  useEffect(() => {
    if (!wide) return undefined;
    const onKey = event => {
      if (event.key !== 'Escape') return;
      event.stopPropagation(); // 뒤의 그룹 모달까지 같이 닫히면 하던 일을 잃는다
      setWide(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [wide]);

  const [expandedTemplateKey, setExpandedTemplateKey] = useState(null);
  const [expandedClassKey, setExpandedClassKey] = useState(null);
  const [planOpenKey, setPlanOpenKey] = useState(null); // 반별 '다가오는 일정' 펼침
  const [peopleOpenKey, setPeopleOpenKey] = useState(null); // 참여자 전체 펼친 회차
  const [typeFilter, setTypeFilter] = useState([]);
  const [templateFilter, setTemplateFilter] = useState([]); // 그룹수업(템플릿) id
  const [trainerFilter, setTrainerFilter] = useState([]); // 담당 강사 id

  const toggleIn = (setter, value) =>
    setter(prev => (prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]));

  const visibleRows = useMemo(() => {
    const trimmed = keyword.trim();
    return rows.filter(row => {
      if (!matchesStatusView(row, statusView)) return false;
      if (typeFilter.length && !typeFilter.includes(row.classType)) return false;
      if (templateFilter.length && !templateFilter.includes(row.templateId)) return false;
      if (trainerFilter.length && !trainerFilter.includes(row.trainerId)) return false;
      if (!trimmed) return true;
      return (
        (row.memberName || '').includes(trimmed) || classNameOf(row).includes(trimmed)
      );
    });
  }, [rows, keyword, statusView, typeFilter, templateFilter, trainerFilter]);

  /*
   * 회원별·묶음 집계는 상태 필터를 빼고 센다. 상태=출석만 걸어 두면 분모(출석+결석)에서
   * 결석이 통째로 빠져 전원 100%, 결석만 걸면 전원 0% 가 되어 출석률이 거짓이 된다.
   * 검색어·유형은 '무엇을 볼지'를 좁히는 것이라 그대로 반영한다.
   */
  const statRows = useMemo(() => {
    const trimmed = keyword.trim();
    return rows.filter(row => {
      if (typeFilter.length && !typeFilter.includes(row.classType)) return false;
      if (templateFilter.length && !templateFilter.includes(row.templateId)) return false;
      if (trainerFilter.length && !trainerFilter.includes(row.trainerId)) return false;
      if (!trimmed) return true;
      return (row.memberName || '').includes(trimmed) || classNameOf(row).includes(trimmed);
    });
  }, [rows, keyword, typeFilter, templateFilter, trainerFilter]);

  /*
   * 필터 선택지는 지금 조회된 내역에서 뽑는다. 지점 전체 목록을 따로 부르면 이 그룹이
   * 듣지도 않은 수업·강사가 잔뜩 나와 고를 게 없어진다.
   */
  const templateOptions = useMemo(() => {
    const map = new Map();
    rows.forEach(row => {
      if (row.templateId == null) return;
      if (!map.has(row.templateId)) map.set(row.templateId, row.templateName || `수업 ${row.templateId}`);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'ko'));
  }, [rows]);

  const trainerOptions = useMemo(() => {
    const map = new Map();
    rows.forEach(row => {
      if (row.trainerId == null) return;
      if (!map.has(row.trainerId)) map.set(row.trainerId, row.trainerName || `강사 ${row.trainerId}`);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'ko'));
  }, [rows]);

  // 방문은 상태·유형 필터와 무관하다. 검색어(회원명·상품명)만 적용한다.
  /*
   * 방문 구분 (#92). all=전체, class=수업 예약으로 체크인한 입장(linkedToReservation),
   * facility=수업 없이 시설만 이용한 입장. 서버 필드를 그대로 가르기만 한다.
   */
  const [visitKind, setVisitKind] = useState('all');
  /* 상품 축 필터 (#92 재구성). 이용권은 자유 입장 상품이라 이 축이 곧 회원의 성격을 가른다. */
  const [visitTicketKind, setVisitTicketKind] = useState('all'); // all | COURSE_PASS | COURSE_TICKET | TRIAL_TICKET

  /* 감춰질 상품 축을 고른 채로 구분을 바꾸면 목록이 0건인 채 굳는다. 그때만 전체로 되돌린다. */
  const changeVisitKind = kind => {
    setVisitKind(kind);
    if (HIDDEN_TICKET_KIND_BY_VISIT[kind] === visitTicketKind) {
      setVisitTicketKind('all');
    }
  };

  const visibleVisits = useMemo(() => {
    const trimmed = keyword.trim();
    return visits.filter(visit => {
      if (visitKind === 'class' && !visit.linkedToReservation) return false;
      if (visitKind === 'facility' && visit.linkedToReservation) return false;
      if (visitTicketKind !== 'all' && visit.ticketType !== visitTicketKind) return false;
      if (!trimmed) return true;
      return (visit.memberName || '').includes(trimmed)
        || (visit.ticketName || '').includes(trimmed);
    });
  }, [visits, keyword, visitKind, visitTicketKind]);

  /*
   * 이용 통계 (#92 재구성). 이용권 회원에겐 출석/결석이 없다 — 얼마나 자주 오고, 얼마나
   * 머물고, 무엇을 쓰는지가 활동의 지표다. 방문 로그를 회원으로 접는다.
   */
  const usageStats = useMemo(() => {
    const days = Math.max(moment(endDate).diff(moment(startDate), 'days') + 1, 1);
    const weeks = Math.max(days / 7, 1);
    const map = new Map();
    visibleVisits.forEach(visit => {
      const stat = map.get(visit.memberId) || {
        memberId: visit.memberId,
        name: visit.memberName || '-',
        visitDays: 0,
        stayTotal: 0,
        stayCount: 0,
        tickets: new Map(),
        lastVisit: null,
      };
      stat.visitDays += 1;
      if (visit.stayMinutes != null) {
        stat.stayTotal += visit.stayMinutes;
        stat.stayCount += 1;
      }
      if (visit.ticketName) {
        stat.tickets.set(visit.ticketName, (stat.tickets.get(visit.ticketName) || 0) + 1);
      }
      if (!stat.lastVisit || visit.visitDate > stat.lastVisit) stat.lastVisit = visit.visitDate;
      map.set(visit.memberId, stat);
    });
    return [...map.values()]
      .map(stat => ({
        ...stat,
        perWeek: stat.visitDays / weeks,
        avgStay: stat.stayCount ? Math.round(stat.stayTotal / stat.stayCount) : null,
        topTicket: [...stat.tickets.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null,
      }))
      .sort((a, b) => b.visitDays - a.visitDays || a.name.localeCompare(b.name, 'ko'));
  }, [visibleVisits, startDate, endDate]);

  // 상태는 표 위 드롭다운이 따로 보여주므로 여기서 세지 않는다.
  const filterCount =
    (typeFilter.length ? 1 : 0) +
    (templateFilter.length ? 1 : 0) +
    (trainerFilter.length ? 1 : 0);
  const [periodAnchor, setPeriodAnchor] = useState(null);
  // 팝오버는 '조회'를 눌러야 반영한다. 고르는 중에 표가 흔들리지 않게 초안을 따로 든다.
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);

  const openRangePopover = event => {
    setDraftStart(startDate ? new Date(startDate) : null);
    setDraftEnd(endDate ? new Date(endDate) : null);
    setPeriodAnchor(event.currentTarget);
  };

  const closeRangePopover = () => setPeriodAnchor(null);

  const applyRange = () => {
    if (!draftStart || !draftEnd) {
      toast.info(t('시작일과 종료일을 모두 고르세요'));
      return;
    }
    if (!moment(draftStart).isValid() || !moment(draftEnd).isValid()) {
      toast.info(t('날짜를 다시 확인해 주세요'));
      return;
    }
    if (moment(draftStart).isAfter(moment(draftEnd), 'day')) {
      toast.info(t('시작일이 종료일보다 늦습니다'));
      return;
    }
    setPreset(null);
    setStartDate(moment(draftStart).format('YYYY-MM-DD'));
    setEndDate(moment(draftEnd).format('YYYY-MM-DD'));
    setPeriodAnchor(null);
  };

  /*
   * 버튼에는 항상 실제 날짜를 적는다 (#93).
   *
   * 이전에는 프리셋이 켜져 있으면 '기간 조회'라고만 썼는데, '최근 3개월'이 1일부터인지
   * 오늘 기준인지 이름만으로는 알 수 없다는 지적이 나왔다. 날짜를 보여주면 그 자리에서 풀린다.
   */
  const periodLabel = useMemo(() => {
    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid()) return `${startDate || '-'} ~ ${endDate || '-'}`;
    return `${start.format('YYYY.MM.DD')} ~ ${end.format(end.isSame(start, 'year') ? 'MM.DD' : 'YYYY.MM.DD')}`;
  }, [startDate, endDate]);

  // 회원별 집계 — 통합(전체) 출석률과 함께, 그 회원이 들은 수업별 세부도 같이 만든다.
  // 출석률 낮은 사람이 위로 오게 정렬한다.
  /*
   * 회원별 집계 — 시안(그룹 출결 회원별 개선.dc, 2026-08-26) 구조.
   * 지각을 출석의 부속이 아니라 1급 상태로 다룬다: 정시출석(onTime)·지각(late)·결석을
   * 따로 세고, 출석률 분자는 정시+지각이다(늦게라도 왔으면 출석). 펼침은 요일 집계 대신
   * 수업별 카드 + 세션 날짜 칩으로 — 어느 날 왔고 어느 날 빠졌는지가 그대로 보인다.
   */
  const memberStats = useMemo(() => {
    const map = new Map();
    statRows.forEach(row => {
      const stat = map.get(row.memberId) || {
        memberId: row.memberId,
        name: row.memberName || '-',
        onTime: 0,
        late: 0,
        absent: 0,
        reserved: 0,
        lateTotalMinutes: 0,
        classes: new Map(),
      };
      const isLate = row.late === true;
      if (row.status === 'ATTENDED') {
        if (isLate) {
          stat.late += 1;
          stat.lateTotalMinutes += row.lateMinutes || 0;
        } else {
          stat.onTime += 1;
        }
      } else if (row.status === 'ABSENT') stat.absent += 1;
      else stat.reserved += 1;

      const classKey = `${row.classType}|${classNameOf(row)}`;
      const cls = stat.classes.get(classKey) || {
        classType: row.classType,
        className: classNameOf(row),
        onTime: 0,
        late: 0,
        absent: 0,
        reserved: 0,
        lateMinutes: 0,
        sessions: [],
        slots: new Map(), // '요일 HH:mm' 빈도 → 일정 라벨
      };
      if (row.status === 'ATTENDED') {
        if (isLate) { cls.late += 1; cls.lateMinutes += row.lateMinutes || 0; } else cls.onTime += 1;
      } else if (row.status === 'ABSENT') cls.absent += 1;
      else cls.reserved += 1;
      cls.sessions.push({
        date: row.classDate,
        status: row.status,
        late: isLate,
        lateMinutes: row.lateMinutes,
        courseClassId: row.courseClassId,
      });
      if (row.classDate && row.startTime) {
        const slot = `${['일', '월', '화', '수', '목', '금', '토'][moment(row.classDate).day()]} ${row.startTime}`;
        cls.slots.set(slot, (cls.slots.get(slot) || 0) + 1);
      }
      stat.classes.set(classKey, cls);
      map.set(row.memberId, stat);
    });
    return [...map.values()]
      .map(stat => {
        const done = stat.onTime + stat.late + stat.absent;
        return {
          ...stat,
          done,
          rate: done ? Math.round(((stat.onTime + stat.late) / done) * 100) : null,
          classes: [...stat.classes.values()]
            .map(cls => {
              const clsDone = cls.onTime + cls.late + cls.absent;
              const topSlot = [...cls.slots.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
              const sorted = cls.sessions.sort((a, b) => (a.date < b.date ? -1 : 1));
              const navTarget = [...sorted].reverse().find(session => session.courseClassId) || null;
              return {
                ...cls,
                done: clsDone,
                navTarget,
                rate: clsDone ? Math.round(((cls.onTime + cls.late) / clsDone) * 100) : null,
                schedule: topSlot ? `${t('매주')} ${topSlot}` : null,
                sessions: sorted,
              };
            })
            .sort((a, b) => b.done + b.reserved - (a.done + a.reserved)),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [statRows, t]);

  /*
   * 수강반(템플릿)별 통계 (#92). 그룹수업은 템플릿으로 묶고, 템플릿이 없는 수업(개인·듀엣·
   * 단발 그룹)은 강사로 묶는다 — 출결 '묶음' 탭에서 확정했던 규칙 그대로다.
   * 반의 수강 인원은 기간 내 그 반에 한 번이라도 잡힌 회원 수다.
   */
  const classStats = useMemo(() => {
    const map = new Map();
    statRows.forEach(row => {
      /*
       * 집계 단위는 '수업'(이름) 이다. 그룹수업(템플릿)은 그 위 계층에서 다시 묶는다 —
       * 템플릿 하나에 수업명이 여럿이라(dev 실측 템플릿 1번에 3개) 템플릿으로 바로 묶으면
       * 무슨 수업인지가 통째로 사라진다.
       */
      const key = `${row.templateId ?? 'N'}|${row.classType}|${classNameOf(row)}`;
      const stat = map.get(key) || {
        key,
        label: classNameOf(row),
        templateId: row.templateId ?? null,
        templateName: row.templateName || null,
        classType: row.classType,
        attended: 0,
        absent: 0,
        reserved: 0,
        lateCount: 0,
        members: new Set(),
        sessions: new Map(), // '날짜|시작시각' → 회차
        slots: new Map(), // '요일 HH:mm' 빈도 → 일정 라벨
      };
      if (row.status === 'ATTENDED') stat.attended += 1;
      else if (row.status === 'ABSENT') stat.absent += 1;
      else stat.reserved += 1;
      if (row.late === true) stat.lateCount += 1;
      stat.members.add(row.memberId);

      const sessionKey = `${row.classDate}|${row.startTime}`;
      const session = stat.sessions.get(sessionKey) || {
        key: sessionKey,
        date: row.classDate,
        startTime: row.startTime,
        className: classNameOf(row),
        courseClassId: row.courseClassId,
        onTime: 0,
        late: 0,
        absent: 0,
        reserved: 0,
        people: [],
      };
      if (row.status === 'ATTENDED') {
        if (row.late === true) session.late += 1; else session.onTime += 1;
      } else if (row.status === 'ABSENT') session.absent += 1;
      else session.reserved += 1;
      session.people.push({
        memberId: row.memberId,
        name: row.memberName || '-',
        status: row.status,
        late: row.late === true,
        lateMinutes: row.lateMinutes,
      });
      stat.sessions.set(sessionKey, session);

      if (row.classDate && row.startTime) {
        const slot = `${['일', '월', '화', '수', '목', '금', '토'][moment(row.classDate).day()]} ${row.startTime}`;
        stat.slots.set(slot, (stat.slots.get(slot) || 0) + 1);
      }
      map.set(key, stat);
    });
    return [...map.values()]
      .map(stat => {
        const done = stat.attended + stat.absent;
        const sessions = [...stat.sessions.values()]
          .map(session => {
            const sessionDone = session.onTime + session.late + session.absent;
            return {
              ...session,
              done: sessionDone,
              // 아무도 출결이 확정되지 않은 회차는 아직 안 열린 날이다.
              isPlan: sessionDone === 0,
              rate: sessionDone
                ? Math.round(((session.onTime + session.late) / sessionDone) * 100)
                : null,
              people: session.people.sort((a, b) => a.name.localeCompare(b.name, 'ko')),
            };
          })
          .sort((a, b) => (a.key < b.key ? -1 : 1));
        const doneSessions = sessions.filter(session => !session.isPlan);
        const planSessions = sessions.filter(session => session.isPlan);
        const topSlot = [...stat.slots.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
        return {
          ...stat,
          memberCount: stat.members.size,
          done,
          onTime: stat.attended - stat.lateCount,
          rate: done ? Math.round((stat.attended / done) * 100) : null,
          schedule: topSlot ? `${t('매주')} ${topSlot}` : null,
          doneSessions,
          planSessions,
          doneCount: doneSessions.length,
          // 평균 참석 인원 — 진행한 회차에 실제로 온 사람 수의 평균이다.
          avgAttend: doneSessions.length
            ? Math.round((stat.attended / doneSessions.length) * 10) / 10
            : 0,
        };
      })
      .sort((a, b) => b.done + b.reserved - (a.done + a.reserved));
  }, [statRows, t]);



  /*
   * 상태 드롭다운은 회원별·수강반에서 '누구를 보여줄지'만 고른다 (사용자 확정).
   * 집계까지 걸면 출석만 볼 때 분모에서 결석이 빠져 전원 100% 가 되므로,
   * 출석률·카운트는 전체 기준을 유지하고 목록에서만 걸러낸다.
   */
  const visibleMemberStats = useMemo(() => {
    if (!statusView) return memberStats;
    return memberStats
      .map(stat => ({
        ...stat,
        classes: stat.classes.filter(cls =>
          cls.sessions.some(session => matchesStatusView(session, statusView))),
      }))
      .filter(stat => stat.classes.length > 0);
  }, [memberStats, statusView]);

  const visibleClassStats = useMemo(() => {
    if (!statusView) return classStats;
    return classStats.filter(stat => {
      if (statusView === 'LATE') return stat.lateCount > 0;
      if (statusView === 'ATTENDED') return stat.attended > 0;
      if (statusView === 'ABSENT') return stat.absent > 0;
      return stat.reserved > 0;
    });
  }, [classStats, statusView]);

  /*
   * 그룹수업(템플릿) → 그 안의 수업들 (2026-08-27 사용자 확정).
   *
   * 한 줄짜리 표로는 '그냥 수업'과 '그룹수업 템플릿'이 구분되지 않았다. 템플릿을 상위
   * 줄로 세우고 수업을 그 아래로 들여쓴다. 템플릿이 없는 수업은 '그룹 미지정' 으로 모은다.
   */
  const classTree = useMemo(() => {
    const templates = new Map();
    const ungrouped = [];
    visibleClassStats.forEach(stat => {
      if (stat.templateId == null) {
        ungrouped.push(stat);
        return;
      }
      const node = templates.get(stat.templateId) || {
        key: `T${stat.templateId}`,
        name: stat.templateName || t('이름 없는 그룹수업'),
        classType: stat.classType,
        classes: [],
        attended: 0,
        absent: 0,
        reserved: 0,
        lateCount: 0,
        members: new Set(),
      };
      node.classes.push(stat);
      node.attended += stat.attended;
      node.absent += stat.absent;
      node.reserved += stat.reserved;
      node.lateCount += stat.lateCount;
      stat.members.forEach(id => node.members.add(id));
      templates.set(stat.templateId, node);
    });
    const rows = [...templates.values()].map(node => {
      const done = node.attended + node.absent;
      return {
        ...node,
        classes: node.classes.sort((a, b) => b.done + b.reserved - (a.done + a.reserved)),
        done,
        onTime: node.attended - node.lateCount,
        // 인원은 수업별 합이 아니라 중복 없는 사람 수다. 두 수업을 듣는 회원이 두 명이 되면 안 된다.
        memberCount: node.members.size,
        rate: done ? Math.round((node.attended / done) * 100) : null,
      };
    }).sort((a, b) => b.done + b.reserved - (a.done + a.reserved));
    return { templates: rows, ungrouped };
  }, [visibleClassStats, t]);


  /*
   * 기간을 바꾸면 그 템플릿·강사가 조회 결과에서 통째로 빠질 수 있다. 값이 남아 있으면
   * 패널에 해제할 칩도 없이 모든 행이 걸러져 '0건 + 필터 1' 로 갇힌다.
   */
  useEffect(() => {
    if (isLoading) return;
    const ids = new Set(templateOptions.map(([id]) => id));
    setTemplateFilter(prev => (prev.every(id => ids.has(id)) ? prev : prev.filter(id => ids.has(id))));
  }, [templateOptions, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    const ids = new Set(trainerOptions.map(([id]) => id));
    setTrainerFilter(prev => (prev.every(id => ids.has(id)) ? prev : prev.filter(id => ids.has(id))));
  }, [trainerOptions, isLoading]);

  /*
   * 출석부 — 학교 출석부와 같은 격자다. 행은 회원, 열은 날짜(또는 월).
   * 다만 칸이 묻는 것은 "무슨 수업을 들었나"가 아니라 **그날 센터에 왔는가**다.
   * 수업 단위 출결은 내역 뷰가 맡는다.
   *
   * 방문의 정의는 CRM 전체와 같다 — 센터 입장기록 ∪ 예약 출석, 하루 1회.
   * 회원목록 '누적 방문수'와 같은 산식이라 화면끼리 숫자가 어긋나지 않는다.
   * 출석한 날의 절반 가까이가 입장기록 없이 수기로 처리되므로, 입장기록만으로
   * 그리면 왔던 날이 안 온 날처럼 보인다.
   *
   * 예약해 놓고 안 온 날은 ✕로 남긴다. 학교 출석부에서 결석이 그렇듯, 안 온 날이
   * 그냥 빈칸이면 볼 이유가 없는 표가 된다.
   */
  const book = useMemo(
    () => buildAttendanceBook({ statRows, attendDays, members, keyword, startDate, endDate, t }),
    [statRows, attendDays, members, keyword, startDate, endDate, t],
  );

  /* 출석부 요약 — 격자와 같은 기준으로 센다(방문은 하루 한 번). */
  const bookSummary = useMemo(() => {
    const roster = book?.members ?? [];
    return {
      visits: roster.reduce((sum, member) => sum + member.visited, 0),
      visitedMembers: roster.filter(member => member.visited > 0).length,
      absent: roster.reduce((sum, member) => sum + member.absent, 0),
      never: roster.filter(member => member.visited === 0).length,
    };
  }, [book]);

  // 회원별 뷰에서 펼쳐 둔 회원
  const [expandedId, setExpandedId] = useState(null);

  // 끊어 그리기 — 목록·뷰·조건이 바뀌면 처음부터 다시 센다.
  const [renderCount, setRenderCount] = useState(RENDER_CHUNK);
  useEffect(() => {
    setRenderCount(RENDER_CHUNK);
  }, [view, keyword, statusView, typeFilter, templateFilter, trainerFilter, startDate, endDate]);

  const handleScroll = event => {
    const total = view === 'visits' ? visibleVisits.length : visibleRows.length;
    if (renderCount >= total) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) setRenderCount(prev => prev + RENDER_CHUNK);
  };

  /*
   * 표 바로 위 줄. 왼쪽 유형 칩은 회원별에만, 오른쪽 상태 드롭다운은 회원별·내역 공용이다.
   * 칩이 없는 탭에서도 같은 줄을 깔아야 표 시작 높이가 어긋나지 않는다.
   */
  const renderTableTop = (withChips) => (
    <TableTopRow>
      <Box sx={{ display: 'flex', gap: '5px' }}>
        {withChips && (
          <>
            <MemberFilterChip isActive={typeFilter.length === 0} onClick={() => setTypeFilter([])}>
              {t('전체')}
            </MemberFilterChip>
            {Object.entries(TYPE_LABEL).map(([value, label]) => (
              <MemberFilterChip
                key={value}
                isActive={typeFilter.includes(value)}
                onClick={() => toggleIn(setTypeFilter, value)}>
                {t(label)}
              </MemberFilterChip>
            ))}
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <StatusSelect
          aria-label={t('상태로 좁히기')}
          value={statusView}
          onChange={event => setStatusView(event.target.value)}>
          {STATUS_VIEW_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{t(option.label)}</option>
          ))}
        </StatusSelect>
        {/* 표 바로 위에 둬야 '이 표를 크게 본다'가 읽힌다. 툴바에 있을 땐 무엇이 커지는지 몰랐다. */}
        <Tooltip arrow placement="top" title={wide ? t('원래 크기로 되돌립니다') : t('표를 화면 전체로 넓혀 봅니다')}>
          <WideToggle isActive={wide} aria-pressed={wide} onClick={() => setWide(prev => !prev)}>
            <OpenInFullIcon className="wide-icon" sx={{ fontSize: 15 }} />
            {wide ? t('작게 보기') : t('크게 보기')}
          </WideToggle>
        </Tooltip>
      </Box>
    </TableTopRow>
  );

  /*
   * 방문·이용 통계의 표 상단. 두 화면은 같은 입장 기록을 같은 필터로 보고 다르게 묶기만 한다.
   * 그래서 필터를 공유하고, 전환도 이 줄 오른쪽에서 한다 — 위 탭 줄에 떨어져 있으면
   * 이용 통계가 이 필터를 따른다는 게 안 보였다.
   * 여백은 다른 탭의 TableTopRow 와 같아야 빈 화면 문구가 탭마다 위아래로 튀지 않는다.
   */
  const renderVisitTop = () => (
    <TableTopRow sx={{ flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        {[['all', '전체'], ['class', '수업 방문'], ['facility', '시설 이용']].map(([kind, label]) => (
          <AttendanceViewTab key={kind} isActive={visitKind === kind} onClick={() => changeVisitKind(kind)}>
            {t(label)}
          </AttendanceViewTab>
        ))}
        <span style={{ width: '1px', height: '16px', background: '#E3E8EA', margin: '0 2px' }} />
        {/* 상품 축 — 이용권은 자유 입장 상품이라 이 축이 곧 회원 성격을 가른다. */}
        {VISIT_TICKET_OPTIONS
          .filter(([kind]) => HIDDEN_TICKET_KIND_BY_VISIT[visitKind] !== kind)
          .map(([kind, label]) => (
            <AttendanceViewTab key={kind} isActive={visitTicketKind === kind} onClick={() => setVisitTicketKind(kind)}>
              {t(label)}
            </AttendanceViewTab>
          ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {[['visits', '방문 목록'], ['usage', '이용 통계']].map(([target, label]) => (
          <AttendanceViewTab key={target} isActive={view === target} onClick={() => setView(target)}>
            {t(label)}
          </AttendanceViewTab>
        ))}
      </Box>
    </TableTopRow>
  );

  // 지난 수업(출석+결석)만 그리는 미니 막대. 출석률 수치와 같은 그림이 되게 한다.
  /*
   * 요약은 지금 화면에 뿌리는 것과 같은 모집단을 센다. 내역 뷰는 상태 필터가 걸린 목록을,
   * 회원별 뷰는 집계용 statRows 를 본다 — 아니면 카드는 출석률 100%인데 아래 표엔 60%대
   * 회원이 늘어서는 일이 생긴다.
   */
  // 방문 뷰에서는 화면에 쓰지 않으므로 세지 않는다.
  const summarySource =
    view === 'visits' ? EMPTY_ROWS : view === 'members' || view === 'book' ? statRows : visibleRows;

  // 방문 뷰는 출결 지표가 아니라 방문 자체를 센다.
  const visitSummary = useMemo(() => {
    const withStay = visibleVisits.filter(visit => visit.stayMinutes != null);
    const stayTotal = withStay.reduce((sum, visit) => sum + visit.stayMinutes, 0);
    return {
      count: visibleVisits.length,
      members: new Set(visibleVisits.map(visit => visit.memberId)).size,
      reentry: visibleVisits.filter(visit => visit.reentry).length,
      avgStay: withStay.length ? Math.round(stayTotal / withStay.length) : null,
    };
  }, [visibleVisits]);
  const summary = useMemo(() => {
    const attended = summarySource.filter(r => r.status === 'ATTENDED').length;
    const late = summarySource.filter(r => r.late === true).length;
    return {
      attended,
      late,
      onTime: attended - late, // 지각은 출석의 부분집합 — 정시는 그 나머지다
      absent: summarySource.filter(r => r.status === 'ABSENT').length,
      reserved: summarySource.filter(r => r.status === 'RESERVED').length,
    };
  }, [summarySource]);

  // 출석률은 '지나간 수업(출석+결석)' 기준이다. 아직 안 온 예약은 분모에서 뺀다.
  const done = summary.attended + summary.absent;
  const attendedRate = done ? Math.round((summary.attended / done) * 100) : null;
  // 미니 막대는 지난 수업(출석 vs 결석)만 그린다. 예약을 섞으면 출석률과 다른
  // 그림이 되어 "뭘 보여주는지 모르겠는" 상태가 된다.


  /* 출석률 링 — 단일 비율 게이지다. 둘레 대비 dasharray 로 채운다. */

  const applyPreset = item => {
    setPreset(item.key);
    const range = presetRangeOf(item);
    setStartDate(range.start.format('YYYY-MM-DD'));
    setEndDate(range.end.format('YYYY-MM-DD'));
  };

  // 그룹레슨은 그룹 스케줄 화면이 focusClassTimeId/focusDate 를 받아 스크롤·하이라이트까지 해 준다.
  // 개인·듀엣은 그 처리가 없어 날짜만 맞춰 보낸다.
  const goToClass = row => {
    if (!row.courseClassId) {
      toast.info(t('이동할 수업 정보를 찾지 못했습니다'));
      return;
    }
    onNavigate?.();
    if (row.classType === 'GROUP_LESSON') {
      navigate(`/group?focusClassTimeId=${row.courseClassId}&focusDate=${row.classDate}`);
      return;
    }
    navigate(`/private?date=${row.classDate}&focusClassTimeId=${row.courseClassId}`);
  };

  /*
   * 수업 한 줄. 그룹수업 아래에서는 들여쓰고, 미지정에서는 그대로 선다.
   * 펼치면 회차가 나온다 — 표에서 두 단계(그룹수업 > 수업)를 다 보고 나서야 회차다.
   */
  const renderClassRow = (stat, nested) => {
    const isExpanded = expandedClassKey === stat.key;
    const planOpen = planOpenKey === stat.key;
    return (
      <React.Fragment key={stat.key}>
        <MRow
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          onKeyDown={event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            setExpandedClassKey(isExpanded ? null : stat.key);
          }}
          onClick={() => setExpandedClassKey(isExpanded ? null : stat.key)}
          sx={{
            gridTemplateColumns: CLASS_GRID,
            cursor: 'pointer',
            paddingLeft: nested ? '40px' : '18px',
            backgroundColor: isExpanded ? '#F2FBFA' : undefined,
            '&:hover': { backgroundColor: isExpanded ? '#F2FBFA' : '#F8FAFB' },
          }}>
          <span style={{ display: 'grid', gridTemplateColumns: '20px 1fr', alignItems: 'center', minWidth: 0 }}>
            <ExpandChevron isOpen={isExpanded}>▶</ExpandChevron>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', minWidth: 0 }}>
              <ClassTypeChip classType={stat.classType}>
                {TYPE_LABEL[stat.classType] || '-'}
              </ClassTypeChip>
              <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {stat.label}
              </span>
              {stat.schedule && (
                <span style={{ fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>{stat.schedule}</span>
              )}
            </span>
          </span>
          <span style={{ textAlign: 'center', fontSize: '13.5px', fontWeight: 800,
            color: stat.rate == null ? '#C4CBD2' : stat.rate >= 70 ? '#0F8A80' : '#C2453A' }}>
            {stat.rate == null ? '-' : `${stat.rate}%`}
          </span>
          <span style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', background: '#EFF2F4' }}>
            {stat.done > 0 && (
              <>
                <span style={{ width: `${(stat.onTime / stat.done) * 100}%`, background: '#14A79C' }} />
                <span style={{ width: `${(stat.lateCount / stat.done) * 100}%`, background: '#E09A26' }} />
                <span style={{ width: `${(stat.absent / stat.done) * 100}%`, background: '#E05B4B' }} />
              </>
            )}
          </span>
          <span style={{ textAlign: 'center', fontSize: '13px', color: '#374151', fontWeight: 600 }}>
            {`${stat.attended} / ${stat.absent}`}
          </span>
          <span style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600,
            color: stat.lateCount > 0 ? '#B45309' : '#C4CBD2' }}>
            {stat.lateCount > 0 ? `${stat.lateCount}${t('회')}` : '-'}
          </span>
          <span style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280' }}>
            {stat.memberCount ? `${stat.memberCount}${t('명')}` : '-'}
          </span>
        </MRow>
                  {/* 펼침 — 회차 하나가 카드 한 줄. 그 날 누가 왔는지까지 그대로 보인다. */}
                  {isExpanded && (
                    <div style={{ background: '#F8FAFB', borderBottom: '1px solid #EFF2F4',
                      padding: '14px 18px 16px 36px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>
                        {t('진행한 수업')} {stat.doneCount}{t('회')} · {t('평균 참석')} {stat.avgAttend}{t('명')}
                      </div>

                      {/* 아직 안 열린 회차는 따로 접어둔다. 지난 기록 사이에 섞이면 성적처럼 읽힌다. */}
                      {stat.planSessions.length > 0 && (
                        <div style={{ border: '1px dashed #C4CBD2', borderRadius: '10px', background: '#fff', overflow: 'hidden' }}>
                          <ButtonBase
                            onClick={() => setPlanOpenKey(planOpen ? null : stat.key)}
                            aria-expanded={planOpen}
                            sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 16px', justifyContent: 'flex-start',
                              '&:hover': { background: '#F6F8F9' } }}>
                            <ExpandChevron isOpen={planOpen}>▶</ExpandChevron>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#4B5563' }}>
                              {t('예정')} {stat.planSessions.length}{t('회')}
                            </span>
                            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                              {stat.planSessions.length === 1
                                ? dayDateLabel(stat.planSessions[0].date)
                                : `${dayDateLabel(stat.planSessions[0].date)} ~ ${dayDateLabel(stat.planSessions[stat.planSessions.length - 1].date)}`}
                            </span>
                            <span style={{ flex: 1 }} />
                            <span style={{ fontSize: '11.5px', color: '#9CA3AF', fontWeight: 600 }}>{t('다가오는 일정')}</span>
                          </ButtonBase>
                          {planOpen && (
                            <div style={{ borderTop: '1px dashed #DBE1E6', padding: '8px 16px 12px',
                              display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {stat.planSessions.map(session => (
                                <div key={session.key} style={{ display: 'flex', alignItems: 'center', gap: '16px',
                                  padding: '6px 0', borderBottom: '1px solid #F4F6F7' }}>
                                  <span style={{ width: '88px', flexShrink: 0, fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>
                                    {dayDateLabel(session.date)}
                                  </span>
                                  <span style={{ fontSize: '12.5px', color: '#8A94A0' }}>
                                    {session.reserved}{t('명 예약')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/*
                        그룹수업(템플릿) 하나에 수업명이 여럿이라 이름으로 한 겹 더 묶는다
                        (dev 실측 템플릿 1개에 'ㅇㅇㅇ'·'그룹수업수정'·'그룹수업테스트2').
                        이름이 하나이고 그게 행 제목과 같을 때만 머리를 접는다 — 그때만
                        같은 말을 두 번 하는 것이고, 다르면 어느 수업인지 알려 줘야 한다.
                      */}
                      {stat.nameGroups.map(bucket => (
                        <div key={bucket.name} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {(stat.nameGroups.length > 1 || bucket.name !== stat.label) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '2px 2px 0' }}>
                              <AttendanceClassLink
                                title={t('해당 수업으로 이동')}
                                onClick={() => goToClass({
                                  courseClassId: bucket.courseClassId,
                                  classType: stat.classType,
                                  classDate: bucket.sessions[bucket.sessions.length - 1]?.date,
                                })}
                                style={{ fontSize: '13px', fontWeight: 800 }}>
                                {bucket.name}
                              </AttendanceClassLink>
                              <span style={{ fontSize: '12px', color: '#8A94A0' }}>
                                {bucket.sessions.length}{t('회')}
                              </span>
                              <span style={{ flex: 1 }} />
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px',
                                fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {[[bucket.onTime, '출석', '#0F8A80'],
                                  [bucket.late, '지각', '#B45309'],
                                  [bucket.absent, '결석', '#C2453A']]
                                  .filter(([count]) => count > 0)
                                  .map(([count, label, color]) => (
                                    <span key={label} style={{ color }}>{t(label)} {count}</span>
                                  ))}
                              </span>
                              <span style={{ minWidth: '38px', textAlign: 'right', fontSize: '12.5px', fontWeight: 800,
                                color: bucket.rate == null ? '#C4CBD2' : bucket.rate >= 70 ? '#0F8A80' : '#C2453A' }}>
                                {bucket.rate == null ? '-' : `${bucket.rate}%`}
                              </span>
                            </div>
                          )}
                          {bucket.sessions.map(session => (

                        <div key={session.key} style={{ background: '#fff', border: '1px solid #E8ECEF',
                          borderRadius: '10px', padding: '11px 16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <div style={{ width: '88px', flexShrink: 0, fontSize: '13px', fontWeight: 700, color: '#1F2937' }}>
                            {dayDateLabel(session.date)}
                          </div>
                          <div style={{ width: '52px', flexShrink: 0, textAlign: 'right', fontSize: '13.5px', fontWeight: 800,
                            color: session.rate >= 70 ? '#0F8A80' : '#C2453A' }}>
                            {`${session.rate}%`}
                          </div>
                          <div style={{ width: '110px', flexShrink: 0, display: 'flex', height: '8px',
                            borderRadius: '4px', overflow: 'hidden', background: '#EFF2F4' }}>
                            <span style={{ width: `${(session.onTime / session.done) * 100}%`, background: '#14A79C' }} />
                            <span style={{ width: `${(session.late / session.done) * 100}%`, background: '#E09A26' }} />
                            <span style={{ width: `${(session.absent / session.done) * 100}%`, background: '#E05B4B' }} />
                          </div>
                          <div style={{ width: '88px', flexShrink: 0, fontSize: '12.5px', color: '#374151', fontWeight: 600 }}>
                            {t('참석')} {session.onTime + session.late}/{session.done}
                          </div>
                          <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'flex-end' }}>
                            {(peopleOpenKey === `${stat.key}|${session.key}`
                              ? session.people
                              : session.people.slice(0, PEOPLE_CHIP_LIMIT)).map(person => {
                              const tone = person.status === 'ATTENDED'
                                ? (person.late ? SESSION_CHIP.late : SESSION_CHIP.att)
                                : person.status === 'ABSENT' ? SESSION_CHIP.abs : SESSION_CHIP.plan;
                              const state = person.status === 'ATTENDED'
                                ? (person.late ? `${t('지각')} ${stayLabel(person.lateMinutes)}` : t('출석'))
                                : STATUS_LABEL[person.status] || person.status;
                              return (
                                <Tooltip key={person.memberId} arrow placement="top" title={`${person.name} · ${state}`}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px',
                                    padding: '3px 9px', borderRadius: '7px', fontSize: '12px', fontWeight: 600,
                                    background: tone.bg, color: tone.color,
                                    border: `1px ${tone.dashed ? 'dashed' : 'solid'} ${tone.border}` }}>
                                    {person.name}
                                  </span>
                                </Tooltip>
                              );
                            })}
                            {/* 한 반에 스무 명이면 이름표가 벽이 된다. 넘치는 만큼은 접어 둔다. */}
                            {session.people.length > PEOPLE_CHIP_LIMIT && (
                              <ButtonBase
                                onClick={() => setPeopleOpenKey(
                                  peopleOpenKey === `${stat.key}|${session.key}`
                                    ? null
                                    : `${stat.key}|${session.key}`)}
                                sx={{ padding: '3px 9px', borderRadius: '7px', fontSize: '12px',
                                  fontWeight: 700, color: '#4B5563', background: '#EEF1F4',
                                  border: '1px solid #DCE2E6', whiteSpace: 'nowrap',
                                  '&:hover': { background: '#E4E9ED' } }}>
                                {peopleOpenKey === `${stat.key}|${session.key}`
                                  ? t('접기')
                                  : `+${session.people.length - PEOPLE_CHIP_LIMIT}`}
                              </ButtonBase>
                            )}
                          </div>
                        </div>
                          ))}
                        </div>
                      ))}

                      {stat.doneSessions.length === 0 && stat.planSessions.length === 0
                        && (
                        <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '4px 2px' }}>
                          {t('선택한 기간에 수업 일정이 없어요')}
                        </div>
                      )}
                    </div>
                  )}
      </React.Fragment>
    );
  };

  // 엑셀 본문은 attendanceExcel.js 로 뺐다. 여기서는 안내만 맡는다.
  const downloadExcel = async () => {
    try {
      await downloadAttendanceExcel({
        view,
        classStats,
        usageStats,
        visibleVisits,
        book,
        memberStats,
        visibleRows,
        group,
        startDate,
        endDate,
      });
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const body = (
    <>
      {/* 기간은 달력 버튼 하나로 접는다. 프리셋이 8개라 늘어놓으면 툴바가 무너진다. */}
      {/* 기간 조회 — 지점 스케줄(개인 스케줄 헤더)의 기간 조회 팝오버와 같은 구조·톤을 쓴다 */}
      <Popover
        open={!!periodAnchor}
        anchorEl={periodAnchor}
        onClose={closeRangePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={{ enter: 180, exit: 120 }}
        slotProps={{
          paper: {
            sx: {
              mt: '6px',
              borderRadius: '12px',
              border: '1px solid #E5E5E5',
              boxShadow: '0 8px 28px rgba(0, 0, 0, 0.12)',
            },
          },
        }}>
        <div style={{ padding: '18px 20px 16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#262626', marginBottom: '12px' }}>
            {t('기간 조회')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CommonDatePicker
              value={draftStart}
              onChange={value => {
                setDraftStart(value);
                // 종료일이 더 이르면 같이 민다. 두 번 고치게 두면 왕복만 는다.
                if (value && draftEnd && moment(value).isAfter(moment(draftEnd), 'day')) {
                  setDraftEnd(value);
                }
              }}
              placeholder="시작일"
              sx={{ width: '164px' }}
            />
            <span style={{ color: '#B0B4BA' }}>~</span>
            <CommonDatePicker
              value={draftEnd}
              onChange={setDraftEnd}
              placeholder="종료일"
              minDate={draftStart}
              sx={{ width: '164px' }}
            />
          </div>
          <div style={{ marginTop: '10px', fontSize: '12px', color: '#8A8F98' }}>
            {t('시작일과 종료일을 고르면 그 기간으로 조회합니다.')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
            <Button
              variant="outlined"
              sx={{
                borderColor: '#ddd',
                color: '#666',
                borderRadius: '8px',
                height: '36px',
                px: 2.5,
                '&:hover': { borderColor: '#bbb', backgroundColor: '#f9f9f9' },
              }}
              onClick={closeRangePopover}>
              {t('취소')}
            </Button>
            <Button
              variant="contained"
              disableElevation
              sx={{
                backgroundColor: '#4AB3BC',
                color: '#fff',
                borderRadius: '8px',
                height: '36px',
                px: 2,
                '&:hover': { backgroundColor: '#3FA2AB' },
              }}
              onClick={applyRange}>
              {t('조회')}
            </Button>
          </div>
        </div>
      </Popover>

      {/* 검색 + 필터 */}
      <MemberFilterRow sx={{ padding: '0 0 8px', flexWrap: 'nowrap' }}>
        <PeriodButton onClick={openRangePopover} isActive={!!periodAnchor}>
          <DateRangeIcon sx={{ fontSize: 16 }} />
          <span className="label">{periodLabel}</span>
        </PeriodButton>

        {/* 빠른 선택은 팝오버 밖에 늘어놓는다. 한 번에 보이면 두 번 누를 일이 없다. */}
        <PresetViewport>
        {presetScroll.left && (
          <PresetEdgeArrow edge="left" aria-label={t('이전 기간 버튼')}
            onClick={() => scrollPresets(-1)}>‹</PresetEdgeArrow>
        )}
        <PeriodChipRow ref={presetRowRef} onScroll={syncPresetScroll}>
          {presets.map(p => (
            <PresetSlot key={p.key}>
              <Tooltip arrow placement="top" title={presetRangeText(p)}>
                <PeriodChip isActive={preset === p.key} onClick={() => applyPreset(p)}>
                  {t(p.label)}
                </PeriodChip>
              </Tooltip>
              {/* 전체는 어느 지점에나 있어야 하므로 지울 수 없다. */}
              {p.id != null && (
                <PresetRemove
                  aria-label={`${p.label} ${t('삭제')}`}
                  disabled={removePresetMutation.isPending}
                  onClick={() => removePresetMutation.mutate(p.id)}>
                  ×
                </PresetRemove>
              )}
            </PresetSlot>
          ))}
          {/* 마지막 기간 라벨 바로 뒤에 붙어야 '여기에 더 만든다'로 읽힌다. 줄 안에 둔다. */}
          <PresetArrow aria-label={t('기간 버튼 추가')} onClick={e => setPresetAnchor(e.currentTarget)}>
            ＋
          </PresetArrow>
        </PeriodChipRow>
        {presetScroll.right && (
          <PresetEdgeArrow edge="right" aria-label={t('다음 기간 버튼')}
            onClick={() => scrollPresets(1)}>›</PresetEdgeArrow>
        )}
        </PresetViewport>

        <Popover
          open={!!presetAnchor}
          anchorEl={presetAnchor}
          onClose={() => setPresetAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
          <Box sx={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px', width: '360px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1F2937' }}>
              {t('기간 버튼 추가')}
            </div>

            {/*
              두 갈래다 — 오늘에서 뻗는 상대 기간(시간이 지나도 안 낡는다)과
              달력으로 못 박는 고정 기간(특정 행사 기간용).
            */}
            <ModeSwitch>
              <ModeSwitchButton isActive={newMode === 'relative'} onClick={() => setNewMode('relative')}>
                {t('오늘 기준')}
              </ModeSwitchButton>
              <ModeSwitchButton isActive={newMode === 'fixed'} onClick={() => setNewMode('fixed')}>
                {t('날짜 지정')}
              </ModeSwitchButton>
            </ModeSwitch>

            {newMode === 'relative' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <StatusSelect
                  aria-label={t('방향')}
                  value={newDirection}
                  onChange={event => setNewDirection(event.target.value)}
                  style={{ width: '84px' }}>
                  {PRESET_DIRECTIONS.map(item => (
                    <option key={item.value} value={item.value}>{t(item.label)}</option>
                  ))}
                </StatusSelect>
                <PresetAmountInput
                  type="number"
                  min={1}
                  value={newAmount}
                  onChange={event => setNewAmount(event.target.value)}
                  style={{ width: '72px', flex: 'none' }}
                />
                <StatusSelect
                  aria-label={t('단위')}
                  value={newUnit}
                  onChange={event => setNewUnit(event.target.value)}
                  style={{ width: '84px' }}>
                  {PRESET_UNITS.map(item => (
                    <option key={item.value} value={item.value}>{t(item.label)}</option>
                  ))}
                </StatusSelect>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CommonDatePicker
                  value={newStart}
                  onChange={value => {
                    setNewStart(value);
                    // 종료일이 더 이르면 같이 민다. 두 번 고치게 두면 왕복만 는다.
                    if (value && newEnd && moment(value).isAfter(moment(newEnd), 'day')) {
                      setNewEnd(value);
                    }
                  }}
                  placeholder={t('시작일')}
                  sx={{ width: '150px' }}
                />
                <span style={{ color: '#B0B4BA' }}>~</span>
                <CommonDatePicker
                  value={newEnd}
                  minDate={newStart || undefined}
                  onChange={value => setNewEnd(value)}
                  placeholder={t('종료일')}
                  sx={{ width: '150px' }}
                />
              </Box>
            )}

            <PresetAmountInput
              type="text"
              maxLength={60}
              value={newLabel}
              placeholder={newMode === 'relative' && autoLabel
                ? `${t('버튼 이름')} (${t('비우면')} '${autoLabel}')`
                : t('버튼 이름 (예: 여름 특강)')}
              onChange={event => setNewLabel(event.target.value)}
            />

            <div style={{ fontSize: '11.5px', color: presetError ? '#C2453A' : '#9CA3AF' }}>
              {presetError
                ? t(presetError)
                : newMode === 'relative'
                  ? `${t('누를 때마다')} ${presetRangeText({
                    kind: 'RELATIVE_RANGE',
                    direction: newDirection,
                    amount: Number(newAmount),
                    unit: newUnit,
                  })} ${t('가 잡힙니다')}`
                  : t('고른 기간이 그대로 저장됩니다')}
            </div>

            <Button
              variant="contained"
              size="small"
              disabled={!!presetError || addPresetMutation.isPending}
              onClick={() => addPresetMutation.mutate(newMode === 'relative'
                ? {
                  type: 'PERIOD_PRESET',
                  kind: 'RELATIVE_RANGE',
                  direction: newDirection,
                  amount: Number(newAmount),
                  unit: newUnit,
                  label: newLabel.trim() || null,
                }
                : {
                  type: 'PERIOD_PRESET',
                  kind: 'FIXED_RANGE',
                  label: newLabel.trim(),
                  startDate: moment(newStart).format('YYYY-MM-DD'),
                  endDate: moment(newEnd).format('YYYY-MM-DD'),
                })}
              sx={{ alignSelf: 'flex-end' }}>
              {t('추가')}
            </Button>
          </Box>
        </Popover>

        <MemberFilterCount
          sx={{
            marginLeft: 'auto',
            marginRight: '4px',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            height: '32px',
            padding: '0 11px',
            borderRadius: '7px',
            background: '#F2F5F7',
            border: '1px solid #E3E8EC',
            fontSize: '12.5px',
            fontWeight: 700,
            color: '#4B5563',
            fontVariantNumeric: 'tabular-nums',
          }}>
          {/* 지금 표에 뿌리는 것과 같은 단위를 센다. 회원별은 건이 아니라 명이다. */}
          {view === 'visits'
            ? visitsLoading
              ? '-'
              : visibleVisits.length === visits.length
              ? `${visits.length}${t('건')}`
              : `${visibleVisits.length}${t('건')} / ${visits.length}${t('건')}`
            : view === 'members'
              ? `${memberStats.length}${t('명')}`
              : visibleRows.length === rows.length
                ? `${rows.length}${t('건')}`
                : `${visibleRows.length}${t('건')} / ${rows.length}${t('건')}`}
        </MemberFilterCount>

        {/* 방문은 수업이 아니라 입장 기록이라 상태·유형·수업·강사 필터가 걸릴 자리가 없다. */}
        {view !== 'visits' && (
          <FilterIconButton
            isActive={filterCount > 0 || filterOpen}
            sx={{ flexShrink: 0 }}
            onClick={() => setFilterOpen(prev => !prev)}>
            <FilterListIcon sx={{ fontSize: 15 }} />
            {filterCount > 0 ? `${t('필터')} ${filterCount}` : t('필터')}
          </FilterIconButton>
        )}
        <Tooltip title={!canExcel ? t('권한이 없습니다') : ''} arrow>
          <span style={{ flexShrink: 0 }}>
        <CommonExcelButton
          onClick={downloadExcel}
          disabled={
            !canExcel ||
            view === 'visits'
              ? visitsLoading || visibleVisits.length === 0
              : view === 'members'
                ? memberStats.length === 0
                : visibleRows.length === 0
          }
          sx={{
            minWidth: '74px',
            height: '32px',
            fontSize: '12px',
            // 공용 버튼의 진초록이 이 화면에선 혼자 튄다. 여기서만 톤을 낮춘다.
            backgroundColor: '#fff',
            color: '#4B5563',
            border: '1px solid #E0E4E7',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#F7F9FA', borderColor: '#C9D0D5', boxShadow: 'none' },
          }}>
          {t('엑셀')}
        </CommonExcelButton>
          </span>
        </Tooltip>
      </MemberFilterRow>

      {filterOpen && view !== 'visits' && (
        <ConditionPanel sx={{ marginBottom: 0 }}>
          <ConditionGrid>
            <ConditionBlock>
              <ConditionLabel>{t('수업 유형')}</ConditionLabel>
              <ConditionChipRow>
                {Object.entries(TYPE_LABEL).map(([value, label]) => (
                  <MemberFilterChip
                    key={value}
                    isActive={typeFilter.includes(value)}
                    onClick={() => toggleIn(setTypeFilter, value)}>
                    {t(label)}
                  </MemberFilterChip>
                ))}
              </ConditionChipRow>
            </ConditionBlock>

            {/* 그룹수업(템플릿)·담당 강사 — 이 그룹이 실제로 들은 것만 선택지로 낸다. */}
            <ConditionBlock>
              <ConditionLabel>{t('그룹수업')}</ConditionLabel>
              {templateOptions.length === 0 ? (
                <ConditionEmpty>{t('기간 내 그룹수업 없음')}</ConditionEmpty>
              ) : (
                <ConditionChipRow>
                  {templateOptions.map(([id, name]) => (
                    <MemberFilterChip
                      key={id}
                      isActive={templateFilter.includes(id)}
                      onClick={() => toggleIn(setTemplateFilter, id)}>
                      {name}
                    </MemberFilterChip>
                  ))}
                </ConditionChipRow>
              )}
            </ConditionBlock>

            <ConditionBlock>
              <ConditionLabel>{t('담당 강사')}</ConditionLabel>
              {trainerOptions.length === 0 ? (
                <ConditionEmpty>{t('기간 내 담당 강사 없음')}</ConditionEmpty>
              ) : (
                <ConditionChipRow>
                  {trainerOptions.map(([id, name]) => (
                    <MemberFilterChip
                      key={id}
                      isActive={trainerFilter.includes(id)}
                      onClick={() => toggleIn(setTrainerFilter, id)}>
                      {name}
                    </MemberFilterChip>
                  ))}
                </ConditionChipRow>
              )}
            </ConditionBlock>
          </ConditionGrid>
        </ConditionPanel>
      )}

      {/*
        요약 — 질문 하나당 숫자 하나.
        출석률(지난 수업만) · 출석 · 결석 · 예정(다가올 예약). 예약은 출석률 분모 밖이다.
      */}
      {/*
        위계가 위→아래로 흐르게 한다 (2026-08-26 재구성).
        1단 모드(수업 출결/시설 이용) → 2단 서브뷰 → 그 선택의 요약(KPI) → 표.
        전에는 모드·서브뷰가 KPI 오른쪽 구석에 뭉쳐 있어 구조가 읽히지 않았다.
      */}
      <AttendanceTabsBar>
          <AttendanceViewTabs>
            {/* 1단 — 상품 성격이 곧 구분이다. 수업 출결(예약 기반) / 시설 이용(입장 기반). */}
            <ModeSwitch sx={{ marginBottom: '4px' }}>
              <ModeSwitchButton isActive={mode === 'class'} onClick={() => switchMode('class')}>
                {t('수업 출결')}
              </ModeSwitchButton>
              <ModeSwitchButton isActive={mode === 'facility'} onClick={() => switchMode('facility')}>
                {t('시설 이용')}
              </ModeSwitchButton>
            </ModeSwitch>
  
            {/* 2단 — 모드 안의 축. 한 번에 3개만 보인다. */}
            {mode === 'class' ? (
              <>
                <AttendanceViewTab isActive={view === 'rows'} onClick={() => setView('rows')}>
                  {t('내역')}
                </AttendanceViewTab>
                <AttendanceViewTab isActive={view === 'members'} onClick={() => setView('members')}>
                  {t('회원별')}
                </AttendanceViewTab>
                <AttendanceViewTab isActive={view === 'classes'} onClick={() => setView('classes')}>
                  {t('수업별')}
                </AttendanceViewTab>
              </>
            ) : (
              <>
                {/* 이용 통계는 방문의 묶어보기라 같은 탭 아래 둔다. 전환은 표 상단 줄에서 한다. */}
                <AttendanceViewTab
                  isActive={view === 'visits' || view === 'usage'}
                  onClick={() => {
                    setFilterOpen(false); // 방문에는 걸 조건이 없다. 열린 패널을 남기지 않는다.
                    setView('visits');
                  }}>
                  {t('방문')}
                </AttendanceViewTab>
                <AttendanceViewTab isActive={view === 'book'} onClick={() => setView('book')}>
                  {t('출석부')}
                </AttendanceViewTab>
              </>
            )}
          </AttendanceViewTabs>
      </AttendanceTabsBar>

      <AttendanceMain wide={wide}>
      <AttendanceSummaryCard className="attendance-summary">
        {view === 'book' ? (
          <>
            <AttendanceKpi>
              <AttendanceKpiValue sx={{ color: '#0F7F85' }}>{bookSummary.visits}</AttendanceKpiValue>
              <AttendanceKpiLabel>
                <Tooltip arrow placement="top" title={t('센터 입장 기록과 수업 출석을 합쳐 하루 한 번으로 셉니다')}>
                  <span>{t('방문')}</span>
                </Tooltip>
              </AttendanceKpiLabel>
            </AttendanceKpi>
            <AttendanceKpiDivider className="attendance-divider" />
            <AttendanceKpi>
              <AttendanceKpiValue>{bookSummary.visitedMembers}</AttendanceKpiValue>
              <AttendanceKpiLabel>{t('다녀간 회원')}</AttendanceKpiLabel>
            </AttendanceKpi>
            <AttendanceKpi>
              <AttendanceKpiValue sx={{ color: bookSummary.absent ? '#C2403D' : undefined }}>
                {bookSummary.absent}
              </AttendanceKpiValue>
              <AttendanceKpiLabel>{t('예약했지만 안 온 날')}</AttendanceKpiLabel>
            </AttendanceKpi>
            <AttendanceKpi>
              <AttendanceKpiValue>{bookSummary.never}</AttendanceKpiValue>
              <AttendanceKpiLabel>{t('한 번도 안 온 회원')}</AttendanceKpiLabel>
            </AttendanceKpi>
          </>
        ) : view === 'visits' || view === 'usage' ? (
          <>
            <AttendanceKpi>
              <AttendanceKpiValue>{visitSummary.count}</AttendanceKpiValue>
              <AttendanceKpiLabel>{t('방문')}</AttendanceKpiLabel>
            </AttendanceKpi>
            <AttendanceKpiDivider className="attendance-divider" />
            <AttendanceKpi>
              <AttendanceKpiValue>{visitSummary.members}</AttendanceKpiValue>
              <AttendanceKpiLabel>{t('방문 회원')}</AttendanceKpiLabel>
            </AttendanceKpi>
            <AttendanceKpi>
              <AttendanceKpiValue>{visitSummary.reentry}</AttendanceKpiValue>
              <AttendanceKpiLabel>{t('재입장')}</AttendanceKpiLabel>
            </AttendanceKpi>
            <AttendanceKpi>
              <AttendanceKpiValue>
                {visitSummary.avgStay == null ? '-' : stayLabel(visitSummary.avgStay)}
              </AttendanceKpiValue>
              <AttendanceKpiLabel>
                <Tooltip arrow placement="top" title={t('퇴실이 찍힌 방문만 평균에 넣습니다')}>
                  <span>{t('평균 이용 시간')}</span>
                </Tooltip>
              </AttendanceKpiLabel>
            </AttendanceKpi>
          </>
        ) : (
          <>
            {/*
              수업 출결 요약 — 시안(그룹 출결 회원별 개선.dc) 레이아웃.
              링 + 제목/보조, 정시출석·지각·결석·예정 4개 숫자, 오른쪽에 범례와 안내.
              지각 #E09A26 은 시안 주황을 검증기 통과 스텝으로 스냅한 값이다.
            */}
            <Tooltip arrow placement="top"
              title={done ? `${t('출석')} ${summary.attended} / ${t('지난 수업')} ${done}` : t('지난 수업 없음')}>
              <AttendanceRing
                rate={attendedRate}
                label={attendedRate == null
                  ? t('지난 수업 없음')
                  : `${t('출석률')} ${attendedRate}%`} />
            </Tooltip>
            <div className="attendance-title" style={{ marginTop: '9px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap' }}>
                {t('그룹 출석률')}<Hint text={t(HINTS.rate)} />
              </div>
              <div style={{ fontSize: '12.5px', color: '#6B7280', marginTop: '2px', whiteSpace: 'nowrap' }}>
                {done ? `${summary.attended}/${done}${t('회')}` : t('지난 수업 없음')}
              </div>
              {statusView && (
                <AttendanceKpiHint>{t('상태 좁혀 보는 중')}</AttendanceKpiHint>
              )}
            </div>

            <AttendanceKpiDivider className="attendance-divider" />

            {[
              [summary.onTime, '출석', '#14A79C'],
              [summary.late, '지각', '#E09A26'],
              [summary.absent, '결석', '#E05B4B'],
              [summary.reserved, '예정', '#5B7B9A'],
            ].map(([value, label, color]) => (
              <AttendanceKpi className="attendance-kpi" key={label} sx={{ minWidth: '44px', alignItems: 'center', mt: '7px' }}>
                <AttendanceKpiValue sx={{ fontSize: '20px', color: value > 0 ? color : '#C4CBD2' }}>
                  {value}
                </AttendanceKpiValue>
                <AttendanceKpiLabel>
                  {t(label)}
                  {label === '예정' && <Hint text={t(HINTS.reserved)} />}
                </AttendanceKpiLabel>
              </AttendanceKpi>
            ))}

            <div style={{ flex: 1 }} />

            <div className="attendance-legend" style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', marginTop: '7px' }}>
              <div style={{ display: 'flex', gap: '14px', fontSize: '12px', color: '#6B7280' }}>
                {[['#14A79C', '출석', false], ['#E09A26', '지각', false], ['#E05B4B', '결석', false], ['', '예정', true]]
                  .map(([color, label, dashed]) => (
                    <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{
                        width: '9px', height: '9px', borderRadius: '3px', boxSizing: 'border-box',
                        background: dashed ? 'transparent' : color,
                        border: dashed ? '1.5px dashed #A8B3BE' : 'none',
                      }} />
                      {t(label)}
                    </span>
                  ))}
              </div>
              {view === 'members' && (
                <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                  {t('회원을 누르면 수업별 참여 날짜가 펼쳐져요')}
                </div>
              )}
            </div>


          </>
        )}

      </AttendanceSummaryCard>

      {/* 표 */}
      <Box className="attendance-table" sx={{ flex: 1, overflowY: 'auto', mt: '2px', minHeight: 0 }} onScroll={handleScroll}>
        {view === 'visits' ? (
          visitsLoading ? (
            <EmptyState>
              <CircularProgress size={22} />
            </EmptyState>
          ) : (
            <>
          {renderVisitTop()}
          {visibleVisits.length === 0 ? (
            <EmptyState>
              <EventBusyOutlinedIcon sx={{ fontSize: 34, color: '#ddd' }} />
              {visits.length === 0 ? (
                <>
                  <EmptyTitle>{t('기간 내 방문 기록이 없습니다')}</EmptyTitle>
                  <EmptyDescription>{t('체크인 앱으로 입장한 기록만 남습니다')}</EmptyDescription>
                </>
              ) : (
                <>
                  <EmptyTitle>{t('검색어에 맞는 방문이 없습니다')}</EmptyTitle>
                  <EmptyDescription>{t('검색어를 지우면 다시 보입니다')}</EmptyDescription>
                </>
              )}
            </EmptyState>
          ) : (
            <>
              <VisitRow header>
                <span>{t('일자')}</span>
                <span>{t('회원')}</span>
                <span>{t('입장 상품')}</span>
                <span>{t('입실')}</span>
                <span>{t('퇴실')}</span>
                <span>{t('이용 시간')}</span>
                <span>{t('입장')}</span>
              </VisitRow>
              {visibleVisits.slice(0, renderCount).map((visit, index) => (
                <VisitRow
                  key={`${visit.memberId}-${visit.visitDate}-${visit.entryTime}-${index}`}
                  isDateStart={index > 0 && visibleVisits[index - 1].visitDate !== visit.visitDate}>
                  <span className="cell-date">{dateWithDay(visit.visitDate)}</span>
                  <span className="cell-name">{visit.memberName || '-'}</span>
                  <span className="cell-ticket">
                    {visit.ticketType && (
                      <AttendanceTypeBadge>
                        {TICKET_TYPE_LABEL[visit.ticketType] || visit.ticketType}
                      </AttendanceTypeBadge>
                    )}
                    <span className="ticket-name">{visit.ticketName || t('상품 기록 없음')}</span>
                    {visit.linkedToReservation && (
                      <Tooltip arrow placement="top" title={t('수업 예약으로 체크인한 입장입니다')}>
                        <VisitLinkedMark>{t('수업')}</VisitLinkedMark>
                      </Tooltip>
                    )}
                  </span>
                  <AttendanceTimeCell empty={!visit.entryTime}>{visit.entryTime || '-'}</AttendanceTimeCell>
                  <AttendanceTimeCell empty={!visit.exitTime}>
                    {visit.exitTime
                      ? `${visit.exitNextDay ? '익일 ' : ''}${visit.exitTime}`
                      : visit.entryTime
                        ? <LateBadge>{t('퇴실 미처리')}</LateBadge>
                        : '-'}
                  </AttendanceTimeCell>
                  <AttendanceTimeCell empty={visit.stayMinutes == null}>
                    {stayLabel(visit.stayMinutes)}
                  </AttendanceTimeCell>
                  <span style={{ display: 'flex', justifyContent: 'center' }}>
                    {/*
                      방문은 입장기록이 뼈대라 이벤트가 없으면 경로를 모른다(#92 보강 이후).
                      '수기 처리'라고 적으면 거짓이 되므로 모르면 '-' 로 둔다.
                    */}
                    {visit.entrySource ? (
                      <EntrySourceBadge tone={entryToneOf(visit)}>{entryLabelOf(visit)}</EntrySourceBadge>
                    ) : (
                      <span style={{ color: '#B6BDC4' }}>-</span>
                    )}
                  </span>
                </VisitRow>
              ))}
            </>
          )}
            </>
          )
        ) : isLoading || (view === 'book' && attendDaysLoading) ? (
          /* 입장기록이 아직이면 다녀간 날이 빈칸으로 보인다. 다 받고 그린다. */
          <EmptyState>
            <CircularProgress size={22} />
          </EmptyState>
        ) : isError ? (
          <>
            {/* '내역이 없습니다' 와 같은 높이에 서도록 표 위 줄 자리를 비워 둔다.
                실패한 화면에서는 유형·상태를 골라 봐야 소용이 없으니 숨긴 채로 깐다. */}
            <Box aria-hidden sx={{ visibility: 'hidden' }}>{renderTableTop(false)}</Box>
            <EmptyState>
              <EventBusyOutlinedIcon sx={{ fontSize: 34, color: '#E8B4B0' }} />
              <EmptyTitle>{t('출결을 불러오지 못했습니다')}</EmptyTitle>
              <EmptyDescription>{t('잠시 후 다시 시도해 주세요')}</EmptyDescription>
            </EmptyState>
          </>
        ) : (view === 'members' ? visibleMemberStats.length === 0
          : view === 'classes' ? visibleClassStats.length === 0
            : visibleRows.length === 0) ? (
          <>
            {/* 0명이어도 방금 누른 칩 줄은 남겨야 되돌릴 수 있다.
                칩이 없는 탭은 같은 줄을 숨긴 채 깔아 빈 화면 높이를 맞춘다. */}
            {view === 'book'
              ? <Box aria-hidden sx={{ visibility: 'hidden' }}>{renderTableTop(false)}</Box>
              : renderTableTop(true)}
            <EmptyState>
            <EventBusyOutlinedIcon sx={{ fontSize: 34, color: '#ddd' }} />
            {rows.length === 0 ? (
              <>
                <EmptyTitle>{t('기간 내 출결 내역이 없습니다')}</EmptyTitle>
                <EmptyDescription>{t('기간을 바꾸거나 다른 그룹을 확인해 보세요')}</EmptyDescription>
              </>
            ) : (
              <>
                <EmptyTitle>{t('조건에 맞는 출결이 없습니다')}</EmptyTitle>
                <EmptyDescription>{t('위 검색어나 필터를 지우면 다시 보입니다')}</EmptyDescription>
              </>
            )}
            </EmptyState>
          </>
        ) : view === 'book' ? (
          <AttendanceBookView book={book} />
        ) : view === 'members' ? (
          <>
            {renderTableTop(true)}
            {/* 시안(그룹 출결 회원별 개선.dc) 레이아웃 — 지각이 별도 색의 1급 상태다. */}
            <MRow header>
              <span style={{ textAlign: 'center', paddingLeft: '20px' }}>{t('회원')}</span>
              <span style={{ textAlign: 'center' }}>{t('출석률')}</span>
              <span style={{ textAlign: 'center' }}>{t('출석 · 지각 · 결석')}</span>
              <span style={{ textAlign: 'center' }}>{t('출석/결석')}</span>
              <span style={{ textAlign: 'center' }}>{t('지각')}</span>
              <span style={{ textAlign: 'center' }}>{t('예정')}</span>
            </MRow>
            {visibleMemberStats.map(stat => {
              const isExpanded = expandedId === stat.memberId;
              return (
                <React.Fragment key={stat.memberId}>
                  <MRow
                    role="button"
                    tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setExpandedId(isExpanded ? null : stat.memberId);
                    }}
                    onClick={() => setExpandedId(isExpanded ? null : stat.memberId)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? '#F2FBFA' : undefined,
                      '&:hover': { backgroundColor: isExpanded ? '#F2FBFA' : '#F8FAFB' },
                    }}>
                    <span style={{ display: 'grid', gridTemplateColumns: '20px 1fr', alignItems: 'center', minWidth: 0 }}>
                      <ExpandChevron isOpen={isExpanded}>▶</ExpandChevron>
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: 0 }}>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {stat.name}
                        </span>
                        <span style={{ fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {stat.classes.length}{t('개 수업')}
                        </span>
                      </span>
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13.5px', fontWeight: 800,
                      color: stat.rate == null ? '#C4CBD2' : stat.rate >= 70 ? '#0F8A80' : '#C2453A' }}>
                      {stat.rate == null ? '-' : `${stat.rate}%`}
                    </span>
                    {/* 정시·지각·결석 3색 스택바 — 지각을 출석 안에 숨기지 않는다. */}
                    <span style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', background: '#EFF2F4' }}>
                      {stat.done > 0 && (
                        <>
                          <span style={{ width: `${(stat.onTime / stat.done) * 100}%`, background: '#14A79C' }} />
                          <span style={{ width: `${(stat.late / stat.done) * 100}%`, background: '#E09A26' }} />
                          <span style={{ width: `${(stat.absent / stat.done) * 100}%`, background: '#E05B4B' }} />
                        </>
                      )}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13px', color: '#374151', fontWeight: 600 }}>
                      {`${stat.onTime + stat.late} / ${stat.absent}`}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600,
                      color: stat.late > 0 ? '#B45309' : '#C4CBD2' }}>
                      {stat.late > 0 ? `${stat.late}${t('회')}` : '-'}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280' }}>
                      {stat.reserved || '-'}
                    </span>
                  </MRow>

                  {/* 펼침 — 수업별 카드 + 세션 날짜 칩. 어느 날 왔고 어느 날 빠졌는지가 그대로 보인다. */}
                  {isExpanded && (
                    <div style={{ background: '#F8FAFB', borderBottom: '1px solid #EFF2F4',
                      padding: '14px 18px 16px 36px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {stat.late > 0 && (
                        <div style={{ fontSize: '12.5px', color: '#B45309', fontWeight: 600 }}>
                          ⏱ {t('기간 내 지각')} {stat.late}{t('회')} · {t('누적')} {stayLabel(stat.lateTotalMinutes)}
                        </div>
                      )}
                      {stat.classes.map(cls => (
                        <div key={`${stat.memberId}-${cls.classType}-${cls.className}`}
                          style={{ background: '#fff', border: '1px solid #E8ECEF', borderRadius: '10px',
                            padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {/*
                            수업명이 길거나 반이 많아도 줄이 무너지지 않게 두 덩이로 나눈다 —
                            왼쪽(이름·일정)만 줄어들고, 오른쪽 숫자 묶음은 폭을 지킨다.
                          */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1 }}>
                              <AttendanceTypeBadge>{TYPE_LABEL[cls.classType] || '-'}</AttendanceTypeBadge>
                              {/* 이름을 누르면 그 반의 가장 최근 회차로 이동해 포커스한다. */}
                              <AttendanceClassLink
                                title={t('해당 수업으로 이동')}
                                onClick={() => goToClass({
                                  courseClassId: cls.navTarget?.courseClassId,
                                  classType: cls.classType,
                                  classDate: cls.navTarget?.date,
                                })}
                                style={{ fontSize: '13.5px', fontWeight: 800,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {cls.className}
                              </AttendanceClassLink>
                              {cls.schedule && (
                                <span style={{ fontSize: '12px', color: '#8A94A0', whiteSpace: 'nowrap',
                                  overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 3 }}>
                                  {cls.schedule}
                                </span>
                              )}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                              {/*
                                'N/M회' 는 분모가 무엇인지(결석 포함인지) 읽는 사람마다 달라진다.
                                출석·지각·결석을 각각 적고 색으로 묶는다. 0 인 항목은 지운다.
                              */}
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px',
                                fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {[[cls.onTime, '출석', '#0F8A80'],
                                  [cls.late, '지각', '#B45309'],
                                  [cls.absent, '결석', '#C2453A']]
                                  .filter(([count]) => count > 0)
                                  .map(([count, label, color]) => (
                                    <span key={label} style={{ color }}>
                                      {t(label)} {count}
                                    </span>
                                  ))}
                                {cls.done === 0 && (
                                  <span style={{ color: '#9CA3AF', fontWeight: 600 }}>
                                    {t('지난 수업 없음')}
                                  </span>
                                )}
                              </span>
                              {cls.late > 0 && (
                                <Tooltip arrow placement="top"
                                  title={`${t('누적 지각')} ${stayLabel(cls.lateMinutes)}`}>
                                  <span style={{ fontSize: '12px', color: '#B45309', fontWeight: 600,
                                    whiteSpace: 'nowrap' }}>
                                    {stayLabel(cls.lateMinutes)}
                                  </span>
                                </Tooltip>
                              )}
                              {/*
                                수업마다 출석률 (#92). 회원 전체 출석률과 같은 산식이다.
                                맨 오른쪽 끝에 두어 카드마다 같은 자리에서 읽히게 한다.
                              */}
                              <span style={{ minWidth: '38px', textAlign: 'right',
                                fontSize: '13px', fontWeight: 800,
                                color: cls.rate == null ? '#C4CBD2' : cls.rate >= 70 ? '#0F8A80' : '#C2453A' }}>
                                {cls.rate == null ? '-' : `${cls.rate}%`}
                              </span>
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {cls.sessions.map((session, index) => {
                              const tone = session.status === 'ATTENDED'
                                ? (session.late ? SESSION_CHIP.late : SESSION_CHIP.att)
                                : session.status === 'ABSENT' ? SESSION_CHIP.abs : SESSION_CHIP.plan;
                              const dateLabel = session.date
                                ? `${moment(session.date).format('M/D')} (${['일', '월', '화', '수', '목', '금', '토'][moment(session.date).day()]})`
                                : '-';
                              return (
                                <Tooltip key={`${session.date}-${index}`} arrow placement="top"
                                  title={`${dateLabel} · ${session.status === 'ATTENDED' ? (session.late ? `${t('지각')} ${stayLabel(session.lateMinutes)}` : t('출석')) : STATUS_LABEL[session.status] || session.status}${session.courseClassId ? ` · ${t('눌러서 이동')}` : ''}`}>
                                  {/* 날짜를 누르면 바로 그 날 수업으로 간다 — 이름은 최근 회차로 가므로 둘이 다르다. */}
                                  <span
                                    role={session.courseClassId ? 'button' : undefined}
                                    tabIndex={session.courseClassId ? 0 : undefined}
                                    onClick={session.courseClassId ? () => goToClass({
                                      courseClassId: session.courseClassId,
                                      classType: cls.classType,
                                      classDate: session.date,
                                    }) : undefined}
                                    onKeyDown={session.courseClassId ? event => {
                                      if (event.key !== 'Enter' && event.key !== ' ') return;
                                      event.preventDefault();
                                      goToClass({
                                        courseClassId: session.courseClassId,
                                        classType: cls.classType,
                                        classDate: session.date,
                                      });
                                    } : undefined}
                                    style={{ display: 'flex', alignItems: 'center', gap: '5px',
                                    padding: '5px 10px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600,
                                    background: tone.bg, color: tone.color,
                                    cursor: session.courseClassId ? 'pointer' : 'default',
                                    border: `1px ${tone.dashed ? 'dashed' : 'solid'} ${tone.border}` }}>
                                    <span>{dateLabel}</span>
                                    {session.late && session.lateMinutes != null && (
                                      <span style={{ fontSize: '11.5px', fontWeight: 700 }}>
                                        {`${t('지각')} ${stayLabel(session.lateMinutes)}`}
                                      </span>
                                    )}
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {stat.classes.length === 0 && (
                        <div style={{ fontSize: '13px', color: '#9CA3AF', padding: '4px 2px' }}>
                          {t('선택한 기간에 수업 내역이 없어요')}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        ) : view === 'usage' ? (
          visitsLoading ? (
            <EmptyState>
              <CircularProgress size={22} />
            </EmptyState>
          ) : usageStats.length === 0 ? (
            <>
              {renderVisitTop()}
              <EmptyState>
                <EventBusyOutlinedIcon sx={{ fontSize: 34, color: '#ddd' }} />
                {visits.length === 0 ? (
                  <>
                    <EmptyTitle>{t('기간 내 방문 기록이 없습니다')}</EmptyTitle>
                    <EmptyDescription>{t('체크인 앱으로 입장한 기록만 남습니다')}</EmptyDescription>
                  </>
                ) : (
                  <>
                    <EmptyTitle>{t('조건에 맞는 방문이 없습니다')}</EmptyTitle>
                    <EmptyDescription>{t('위 필터를 지우면 다시 보입니다')}</EmptyDescription>
                  </>
                )}
              </EmptyState>
            </>
          ) : (
            <>
              {renderVisitTop()}
              <UsageRow sx={{ fontSize: '12px', color: '#999', fontWeight: 600, position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                <span>{t('회원')}</span>
                <span style={{ textAlign: 'center' }}>{t('방문 일수')}</span>
                <span style={{ textAlign: 'center' }}>{t('주당 방문')}</span>
                <span style={{ textAlign: 'center' }}>{t('총 이용 시간')}</span>
                <span style={{ textAlign: 'center' }}>{t('평균 이용 시간')}</span>
                <span>{t('주 이용 상품')}</span>
                <span style={{ textAlign: 'center' }}>{t('마지막 방문')}</span>
              </UsageRow>
              {usageStats.map(stat => (
                <UsageRow key={stat.memberId}>
                  <MemberStatName>{stat.name}</MemberStatName>
                  <span style={{ textAlign: 'center', fontWeight: 700 }}>{stat.visitDays}</span>
                  <span style={{ textAlign: 'center' }}>{stat.perWeek.toFixed(1)}{t('회')}</span>
                  <span style={{ textAlign: 'center' }}>
                    {stat.stayCount ? stayLabel(stat.stayTotal) : '-'}
                  </span>
                  <span style={{ textAlign: 'center' }}>
                    {stat.avgStay == null ? '-' : stayLabel(stat.avgStay)}
                  </span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555' }}
                    title={stat.topTicket || ''}>
                    {stat.topTicket || <span style={{ color: '#B6BDC4' }}>-</span>}
                  </span>
                  <span style={{ textAlign: 'center', color: '#555' }}>{stat.lastVisit || '-'}</span>
                </UsageRow>
              ))}
            </>
          )
        ) : view === 'classes' ? (
          <>
            {renderTableTop(true)}
            {/* 회원별과 같은 골격 — 두 표를 나란히 오가므로 열 정렬·색이 어긋나면 안 된다. */}
            <MRow header sx={{ gridTemplateColumns: CLASS_GRID }}>
              <span style={{ textAlign: 'center', paddingLeft: '20px' }}>{t('그룹수업')}</span>
              <span style={{ textAlign: 'center' }}>{t('출석률')}</span>
              <span style={{ textAlign: 'center' }}>{t('출석 · 지각 · 결석')}</span>
              <span style={{ textAlign: 'center' }}>{t('출석/결석')}</span>
              <span style={{ textAlign: 'center' }}>{t('지각')}</span>
              <span style={{ textAlign: 'center' }}>{t('인원')}</span>
            </MRow>
            {/*
              그룹수업(템플릿) 이 상위 줄, 그 안의 수업이 들여쓴 줄이다.
              한 줄짜리 표에서는 '그냥 수업'과 '그룹수업'이 구분되지 않았다.
            */}
            {classTree.templates.map(node => {
              const open = expandedTemplateKey === node.key;
              return (
                <React.Fragment key={node.key}>
                  <MRow
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onKeyDown={event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setExpandedTemplateKey(open ? null : node.key);
                    }}
                    onClick={() => setExpandedTemplateKey(open ? null : node.key)}
                    sx={{
                      gridTemplateColumns: CLASS_GRID,
                      cursor: 'pointer',
                      backgroundColor: open ? '#F2FBFA' : '#FBFCFD',
                      '&:hover': { backgroundColor: open ? '#F2FBFA' : '#F4F7F8' },
                    }}>
                    <span style={{ display: 'grid', gridTemplateColumns: '20px 1fr', alignItems: 'center', minWidth: 0 }}>
                      <ExpandChevron isOpen={open}>▶</ExpandChevron>
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', minWidth: 0 }}>
                        <GroupClassChip>{t('그룹수업')}</GroupClassChip>
                        <span style={{ fontSize: '14px', fontWeight: 800, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {node.name}
                        </span>
                        <span style={{ fontSize: '12px', color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                          {node.classes.length}{t('개 수업')}
                        </span>
                      </span>
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13.5px', fontWeight: 800,
                      color: node.rate == null ? '#C4CBD2' : node.rate >= 70 ? '#0F8A80' : '#C2453A' }}>
                      {node.rate == null ? '-' : `${node.rate}%`}
                    </span>
                    <span style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', background: '#EFF2F4' }}>
                      {node.done > 0 && (
                        <>
                          <span style={{ width: `${(node.onTime / node.done) * 100}%`, background: '#14A79C' }} />
                          <span style={{ width: `${(node.lateCount / node.done) * 100}%`, background: '#E09A26' }} />
                          <span style={{ width: `${(node.absent / node.done) * 100}%`, background: '#E05B4B' }} />
                        </>
                      )}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13px', color: '#374151', fontWeight: 700 }}>
                      {`${node.attended} / ${node.absent}`}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13px', fontWeight: 600,
                      color: node.lateCount > 0 ? '#B45309' : '#C4CBD2' }}>
                      {node.lateCount > 0 ? `${node.lateCount}${t('회')}` : '-'}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: '13px', color: '#6B7280' }}>
                      {node.memberCount ? `${node.memberCount}${t('명')}` : '-'}
                    </span>
                  </MRow>
                  {open && node.classes.map(stat => renderClassRow(stat, true))}
                </React.Fragment>
              );
            })}

            {classTree.ungrouped.length > 0 && (
              <ClassSectionRow>
                {t('그룹 미지정')}
                <span style={{ fontWeight: 600, color: '#9CA3AF' }}>
                  {t('그룹수업에 묶이지 않은 수업이에요')}
                </span>
              </ClassSectionRow>
            )}
            {classTree.ungrouped.map(stat => renderClassRow(stat, false))}
          </>
        ) : (
          <>
            {renderTableTop(true)}
            <AttendanceHeaderRow>
              <span>{t('일자')}</span>
              <span>{t('시간')}</span>
              <span>{t('수업')}</span>
              <span>{t('회원')}</span>
              <span>{t('담당 강사')}</span>
              <span style={{ textAlign: 'center' }}>{t('상태')}</span>
              <span style={{ textAlign: 'center' }}>{t('입장')}</span>
              <span style={{ textAlign: 'center' }}>{t('입·퇴실')}</span>
            </AttendanceHeaderRow>
            {visibleRows.slice(0, renderCount).map((row, index) => (
              <AttendanceRow
                key={`${row.memberId}-${row.classDate}-${row.startTime}-${index}`}
                enterIndex={index}
                // 날짜가 바뀌는 지점에 선을 둬 스캔 리듬을 만든다.
                isDateStart={index > 0 && visibleRows[index - 1].classDate !== row.classDate}>
                <span className="cell-date">{dateWithDay(row.classDate)}</span>
                <span className="cell-time">{row.startTime}~{row.endTime}</span>
                <span className="cell-class">
                  <AttendanceTypeBadge>{TYPE_LABEL[row.classType] || '-'}</AttendanceTypeBadge>
                  <AttendanceClassLink
                    title={t('해당 수업으로 이동')}
                    onClick={() => goToClass(row)}>
                    {classNameOf(row)}
                  </AttendanceClassLink>
                </span>
                <span className="cell-name">{row.memberName || '-'}</span>
                <span className="cell-trainer">{row.trainerName || '-'}</span>
                <span className="cell-status">
                  <AttendanceStatusBadge status={row.status}>
                    {STATUS_LABEL[row.status] || row.status}
                  </AttendanceStatusBadge>
                  {/* 지각 (#92). 지점 유예 설정이 있어야 서버가 late 를 내려준다.
                      늦은 시간은 라벨을 두 줄로 밀어내므로 툴팁으로 내린다. */}
                  {row.late === true && (
                    <Tooltip arrow placement="top" title={`${t('지각')} ${stayLabel(row.lateMinutes)}`}>
                      <LateBadge>{t('지각')}</LateBadge>
                    </Tooltip>
                  )}
                </span>
                <span style={{ display: 'flex', justifyContent: 'center' }}>
                  {/* 입장 정보는 출석한 행에만 뜻이 있다. 예약·결석 행에 '수기 처리'를
                      붙이면 출석 처리된 것처럼 읽혀 거짓이 된다. */}
                  {row.status === 'ATTENDED' ? (
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        row.entryTicketName ||
                        (row.entrySource ? '' : t('입장 기록 없음 · CRM에서 출석 처리된 건입니다'))
                      }>
                      <EntrySourceBadge tone={entryToneOf(row)}>{entryLabelOf(row)}</EntrySourceBadge>
                    </Tooltip>
                  ) : (
                    <span style={{ color: '#C4CACE', fontSize: '12px' }}>-</span>
                  )}
                </span>
                {/* 운영에서 퇴실은 사실상 안 찍힌다. 두 칸으로 나누면 대시만 늘어서 한 칸에 합쳤다. */}
                <AttendanceTimeCell empty={!row.entryTime}>
                  {row.entryTime
                    ? `${row.entryTime}${row.exitTime ? ` ~ ${row.exitNextDay ? '익일 ' : ''}${row.exitTime}` : ''}`
                    : '-'}
                </AttendanceTimeCell>
              </AttendanceRow>
            ))}
          </>
        )}
      </Box>
      </AttendanceMain>
    </>
  );

  /*
   * 크게 보기는 같은 화면을 덮개에 옮겨 담는다 (#92). 내용을 따로 만들지 않으므로
   * 두 모습이 갈라질 일이 없고, 넓어진 만큼 열 폭이 저절로 펴진다.
   */
  return wide ? <WideOverlay>{body}</WideOverlay> : body;
};

export default MemberGroupAttendanceTab;
